import { useCallback, useEffect, useState } from 'react';
import { ArrowDown, ArrowUp, Archive, ArchiveRestore, Plus, Tags } from 'lucide-react';
import type { Me } from '../api/auth';
import { eventTypesApi } from '../api/eventTypes';
import type { DigestPriority, EventTypeAdmin } from '../api/eventTypes';
import type { EventTypeDefaults } from '../api/eventTypes';
import { ValidationFailure } from '../api/events';
import type { FieldErrors } from '../api/events';
import { EVENT_COLORS, EVENT_COLOR_LABELS, eventColorVar, isEventColor, nextEventColor } from '../brand/eventColors';
import type { EventColorKey } from '../brand/eventColors';
import { AppHeader } from '../ui/AppHeader';
import { Button } from '../ui/Button';
import { Footer, Gutter, Page } from '../ui/Chrome';
import { EmptyState } from '../ui/EmptyState';
import { SelectField, TextAreaField, TextField } from '../ui/Field';

/**
 * Where a unit makes the vocabulary its own: rename `Fundraiser` to `Popcorn Sales`, add
 * `Klondike Derby`, retire `Service Project` because they call it `Conservation Day`, and put the
 * three they actually run at the top of the picker.
 *
 * Until this screen existed, changing the list was a deploy.
 *
 * Every row carries how many events use it, because a rename reaches all of them — past ones
 * included — and a leader should be able to see that before making it rather than after.
 */
export function EventTypesScreen({
  unitId,
  unitName,
  canManage,
  me,
  onNavigate,
}: {
  unitId: string;
  unitName: string;
  /** Officer and above. The API is the enforcement point; this decides what renders. */
  canManage: boolean;
  me: Me;
  onNavigate: (path: string) => void;
}) {
  const [types, setTypes] = useState<EventTypeAdmin[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});

  const [adding, setAdding] = useState(false);
  const [newName, setNewName] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [newParentId, setNewParentId] = useState('');
  // null means "take the parent's", which is what a child starts as. A root starts on whichever
  // swatch the unit has not used yet -- set when the form opens, below.
  const [newColorKey, setNewColorKey] = useState<EventColorKey | null>(null);
  // null means "nobody has said", which reads as the parent's answer and then Standard. Unlike the
  // colour above there is no swatch to pick for them: an unranked kind is the ordinary case.
  const [newPriority, setNewPriority] = useState<DigestPriority | null>(null);

  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState('');
  const [editingDefaults, setEditingDefaults] = useState<EventTypeDefaults>(NO_DEFAULTS);
  const [editingColorKey, setEditingColorKey] = useState<EventColorKey | null>(null);
  const [editingPriority, setEditingPriority] = useState<DigestPriority | null>(null);

  const load = useCallback(async () => {
    try {
      setTypes(await eventTypesApi.listAll(unitId));
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load the event types.');
    }
  }, [unitId]);

  useEffect(() => {
    void load();
  }, [load]);

  /** One place for the two failure shapes, so every action reports the same way. */
  async function run(action: () => Promise<unknown>) {
    setError(null);
    setFieldErrors({});

    try {
      await action();
      await load();
      return true;
    } catch (e) {
      if (e instanceof ValidationFailure) {
        setFieldErrors(e.errors);
      } else {
        setError(e instanceof Error ? e.message : 'That did not work.');
      }
      return false;
    }
  }

  const live = types?.filter((t) => !t.isArchived) ?? [];
  const archived = types?.filter((t) => t.isArchived) ?? [];

  // Only a root may be a parent: types go two levels deep, so a child cannot have children. The
  // picker offers what is actually allowed rather than letting the server refuse afterwards.
  const roots = live.filter((t) => !t.parentEventTypeId);

  // Live types in reading order -- each root, then whatever refines it. Reordering still sends the
  // whole live set, so this is a rendering concern and not a second source of truth about order.
  const ordered = roots.flatMap((root) => [
    root,
    ...live.filter((t) => t.parentEventTypeId === root.id),
  ]);

  /**
   * Moves a root, carrying whatever refines it.
   *
   * Only roots move: a child's place is under its parent, so an arrow beside one would promise
   * something the list cannot do. What is sent is the whole live order, flattened back out — the
   * server rewrites every position in one transaction, which is what makes two types sharing a
   * place unreachable rather than merely unlikely.
   */
  async function moveRoot(rootId: string, by: number) {
    const from = roots.findIndex((t) => t.id === rootId);
    const to = from + by;
    if (from < 0 || to < 0 || to >= roots.length) return;

    const next = [...roots];
    [next[from], next[to]] = [next[to], next[from]];

    const orderedIds = next.flatMap((root) => [
      root.id,
      ...live.filter((t) => t.parentEventTypeId === root.id).map((t) => t.id),
    ]);

    await run(() => eventTypesApi.reorder(unitId, orderedIds));
  }

  return (
    <Page>
      <AppHeader me={me} currentUnitId={unitId} onNavigate={onNavigate} />

      <section className="rule-canonical">
        <Gutter>
          <div style={{ paddingBlock: 'var(--space-6)' }}>
            <p
              className="t-eyebrow inline-flex items-center gap-2"
              style={{ color: 'var(--color-text-muted)', margin: 0 }}
            >
              <Tags size={14} aria-hidden="true" />
              {unitName}
            </p>
            <h1
              className="t-heading"
              style={{ color: 'var(--color-text-primary)', margin: 'var(--space-2) 0 var(--space-2)' }}
            >
              Kinds of event
            </h1>
            <p
              className="t-body"
              style={{ color: 'var(--color-text-secondary)', margin: 0, maxWidth: '46ch' }}
            >
              Your unit&rsquo;s own words for the things it runs. They appear in the picker when
              somebody adds an event, in the order you put them in here.
            </p>

            {canManage ? (
              <div className="flex flex-wrap gap-3" style={{ marginTop: 'var(--space-4)' }}>
                <Button
                  variant="primary"
                  onClick={() => {
                    // The next unused swatch, offered rather than assigned: the leader sees what
                    // they are about to get and may pick anything else in the grid. The server
                    // applies the same rule when nothing is sent, so the two cannot disagree.
                    if (!adding) setNewColorKey(nextEventColor(roots.map((r) => r.ownColorKey)));
                    setAdding((v) => !v);
                  }}
                >
                  <Plus size={16} aria-hidden="true" />
                  Add a kind
                </Button>
              </div>
            ) : (
              // Not a disabled button. A member is not waiting on a permission that might arrive;
              // naming who does act is a more useful sentence than a grey control (spec §6).
              <p
                className="t-caption"
                style={{ color: 'var(--color-text-secondary)', marginTop: 'var(--space-4)' }}
              >
                Your unit&rsquo;s officers decide what goes on this list. Ask one of them to add
                or change a kind.
              </p>
            )}
          </div>
        </Gutter>
      </section>

      <section className="flex-1" style={{ background: 'var(--color-surface-sunken)' }}>
        <Gutter>
          <div style={{ paddingBlock: 'var(--space-6)', maxWidth: '40rem' }}>
            {error && (
              <p
                className="t-body"
                role="alert"
                style={{
                  background: 'var(--color-status-blocked-surface)',
                  border: '1px solid var(--color-status-blocked)',
                  borderRadius: 'var(--radius-md)',
                  padding: 'var(--space-3)',
                  marginBottom: 'var(--space-4)',
                  color: 'var(--color-text-primary)',
                }}
              >
                {error}
              </p>
            )}

            {adding && canManage && (
              <form
                onSubmit={async (e) => {
                  e.preventDefault();
                  const created = await run(() =>
                    eventTypesApi.create(unitId, {
                      name: newName,
                      description: newDescription.trim() || null,
                      parentEventTypeId: newParentId || null,
                      // A child sends nothing, so it renders whatever it refines. That is what
                      // keeps every kind of campout one colour on the calendar.
                      colorKey: newParentId ? null : newColorKey,
                      digestPriority: newPriority,
                    }),
                  );

                  if (created) {
                    setNewName('');
                    setNewDescription('');
                    setNewParentId('');
                    setNewColorKey(null);
                    setNewPriority(null);
                    setAdding(false);
                  }
                }}
                className="flex flex-col"
                style={{
                  gap: 'var(--space-4)',
                  background: 'var(--color-surface-raised)',
                  borderRadius: 'var(--radius-md)',
                  padding: 'var(--space-4)',
                  marginBottom: 'var(--space-4)',
                }}
              >
                <TextField
                  label="Name"
                  required
                  value={newName}
                  error={fieldErrors.name?.[0]}
                  onChange={(e) => setNewName(e.target.value)}
                  placeholder="Klondike Derby"
                />
                <TextField
                  label="What it is"
                  value={newDescription}
                  onChange={(e) => setNewDescription(e.target.value)}
                  hint="Only for leaders, so the next person knows which one to pick."
                />
                {roots.length > 0 && (
                  <SelectField
                    label="A kind of"
                    value={newParentId}
                    error={fieldErrors.parentEventTypeId?.[0]}
                    onChange={(e) => setNewParentId(e.target.value)}
                    hint="A Backpacking Campout is a Campout. Anything the Campout carries, this gets too."
                  >
                    <option value="">Nothing &mdash; it stands on its own</option>
                    {roots.map((root) => (
                      <option key={root.id} value={root.id}>
                        {root.name}
                      </option>
                    ))}
                  </SelectField>
                )}
                <ColorPicker
                  value={newParentId ? null : newColorKey}
                  onChange={setNewColorKey}
                  inheritsFrom={roots.find((r) => r.id === newParentId) ?? null}
                  alsoUsedBy={usedBy(live, null)}
                  previewName={newName.trim() || 'Klondike Derby'}
                  error={fieldErrors.colorKey?.[0]}
                />
                <PriorityPicker
                  value={newPriority}
                  onChange={setNewPriority}
                  inheritsFrom={roots.find((r) => r.id === newParentId) ?? null}
                  error={fieldErrors.digestPriority?.[0]}
                />
                <div className="flex flex-wrap gap-3">
                  <Button
                    variant="primary"
                    type="submit"
                    disabledReason={newName.trim() ? undefined : 'Give it a name.'}
                  >
                    Add it
                  </Button>
                  <Button variant="secondary" onClick={() => setAdding(false)}>
                    Cancel
                  </Button>
                </div>
              </form>
            )}

            {types && live.length === 0 && (
              <EmptyState
                icon={Tags}
                headline="No kinds yet"
                explanation="Name the things your unit actually runs and they will be there the next time somebody adds an event."
                action={canManage ? { label: 'Add a kind', onClick: () => setAdding(true) } : undefined}
              />
            )}

            {live.length > 0 && (
              <ul className="flex flex-col" style={{ gap: 'var(--space-2)', listStyle: 'none', margin: 0, padding: 0 }}>
                {ordered.map((type) => (
                  <li key={type.id}>
                    <TypeRow
                      type={type}
                      isChild={Boolean(type.parentEventTypeId)}
                      canManage={canManage}
                      isEditing={editingId === type.id}
                      editingName={editingName}
                      editingDefaults={editingDefaults}
                      editingColorKey={editingColorKey}
                      editingPriority={editingPriority}
                      inheritsFrom={roots.find((r) => r.id === type.parentEventTypeId) ?? null}
                      alsoUsedBy={usedBy(live, type.id)}
                      colorError={editingId === type.id ? fieldErrors.colorKey?.[0] : undefined}
                      priorityError={
                        editingId === type.id ? fieldErrors.digestPriority?.[0] : undefined
                      }
                      onEditingColorChange={setEditingColorKey}
                      onEditingPriorityChange={setEditingPriority}
                      nameError={editingId === type.id ? fieldErrors.name?.[0] : undefined}
                      defaultsError={
                        editingId === type.id ? fieldErrors.eventTypeId?.[0] : undefined
                      }
                      onEditingNameChange={setEditingName}
                      onEditingDefaultsChange={(patch) =>
                        setEditingDefaults((d) => ({ ...d, ...patch }))
                      }
                      onStartEdit={() => {
                        setEditingId(type.id);
                        setEditingName(type.name);
                        // Its OWN suggestions, never the resolved ones. Showing what it inherits
                        // in its boxes would turn the next save into a copy that then stops
                        // tracking the parent -- silently, and only visible a rename later.
                        setEditingDefaults(type.ownDefaults);
                        // Its OWN colour too, for the same reason: an empty box on a child says
                        // truthfully that it takes its parent's, and pre-filling the resolved
                        // answer would quietly cut that link on the next save.
                        setEditingColorKey(isEventColor(type.ownColorKey) ? type.ownColorKey : null);
                        // Its OWN priority too, for the third time and the same reason: an empty
                        // box on a child says truthfully that it takes its parent's.
                        setEditingPriority(type.ownDigestPriority);
                        setFieldErrors({});
                      }}
                      onCancelEdit={() => setEditingId(null)}
                      onSaveEdit={async () => {
                        const saved = await run(() =>
                          eventTypesApi.update(unitId, type.id, {
                            name: editingName,
                            // The whole set every time. The rules that make one valid -- a length
                            // or an end time, never both -- only exist across fields, so a
                            // field-by-field patch could reach an invalid pair one call at a time.
                            defaults: editingDefaults,
                            // The empty string clears it back to the parent's. An absent field
                            // means "leave it alone", and one nullable field cannot say both.
                            colorKey: editingColorKey ?? '',
                            // The empty string clears it, exactly as the colour above does.
                            digestPriority: editingPriority ?? '',
                          }),
                        );
                        if (saved) setEditingId(null);
                      }}
                      onArchive={() => run(() => eventTypesApi.update(unitId, type.id, { archived: true }))}
                      // Only roots carry arrows; a child's place is under its parent, and an
                      // arrow beside one would promise something the list cannot do.
                      onMoveUp={
                        !type.parentEventTypeId && roots[0]?.id !== type.id
                          ? () => moveRoot(type.id, -1)
                          : undefined
                      }
                      onMoveDown={
                        !type.parentEventTypeId && roots[roots.length - 1]?.id !== type.id
                          ? () => moveRoot(type.id, 1)
                          : undefined
                      }
                    />
                  </li>
                ))}
              </ul>
            )}

            {archived.length > 0 && (
              <>
                <h2
                  className="t-label"
                  style={{
                    color: 'var(--color-text-muted)',
                    margin: 'var(--space-6) 0 var(--space-2)',
                  }}
                >
                  No longer offered
                </h2>
                <p
                  className="t-caption"
                  style={{ color: 'var(--color-text-secondary)', margin: '0 0 var(--space-3)' }}
                >
                  Out of the picker. Events already filed under these keep them &mdash; nothing on
                  the calendar changed when they were archived.
                </p>

                <ul className="flex flex-col" style={{ gap: 'var(--space-2)', listStyle: 'none', margin: 0, padding: 0 }}>
                  {archived.map((type) => (
                    <li key={type.id}>
                      <TypeRow
                        type={type}
                        canManage={canManage}
                        onRestore={() =>
                          run(() => eventTypesApi.update(unitId, type.id, { archived: false }))
                        }
                      />
                    </li>
                  ))}
                </ul>
              </>
            )}
          </div>
        </Gutter>
      </section>

      <Footer />
    </Page>
  );
}

/** An empty suggestion set, for a row that is not being edited. */
const NO_DEFAULTS: EventTypeDefaults = {
  durationMinutes: null,
  startTimeOfDay: null,
  endTimeOfDay: null,
  locationName: null,
  locationAddress: null,
  description: null,
  requiresRsvp: null,
  requiresForms: null,
  requiresPacking: null,
  requiresTravel: null,
  requiresProgram: null,
};

function TypeRow({
  type,
  isChild,
  canManage,
  isEditing,
  editingName,
  editingDefaults,
  editingColorKey,
  inheritsFrom,
  alsoUsedBy,
  editingPriority,
  nameError,
  defaultsError,
  colorError,
  priorityError,
  onEditingNameChange,
  onEditingDefaultsChange,
  onEditingColorChange,
  onEditingPriorityChange,
  onStartEdit,
  onCancelEdit,
  onSaveEdit,
  onArchive,
  onRestore,
  onMoveUp,
  onMoveDown,
}: {
  type: EventTypeAdmin;
  /** Refines another type. Indented, so the shape is visible without a second list. */
  isChild?: boolean;
  canManage: boolean;
  isEditing?: boolean;
  editingName?: string;
  editingDefaults?: EventTypeDefaults;
  /** What this type sets itself, or null when it takes its parent's. */
  editingColorKey?: EventColorKey | null;
  /** What this type says itself about how much it matters, or null when it takes its parent's. */
  editingPriority?: DigestPriority | null;
  /** The type this one refines, so the picker can offer "the same as that one" by name. */
  inheritsFrom?: EventTypeAdmin | null;
  /** Which other live kinds already read in each colour. Duplicates are allowed, and warned. */
  alsoUsedBy?: Map<string, string[]>;
  nameError?: string;
  /** A refusal about the set as a whole — a length and an end time together, most likely. */
  defaultsError?: string;
  colorError?: string;
  priorityError?: string;
  onEditingNameChange?: (value: string) => void;
  onEditingDefaultsChange?: (patch: Partial<EventTypeDefaults>) => void;
  onEditingColorChange?: (value: EventColorKey | null) => void;
  onEditingPriorityChange?: (value: DigestPriority | null) => void;
  onStartEdit?: () => void;
  onCancelEdit?: () => void;
  onSaveEdit?: () => void;
  onArchive?: () => void;
  onRestore?: () => void;
  onMoveUp?: () => void;
  onMoveDown?: () => void;
}) {
  return (
    <div
      className="flex items-start justify-between gap-3"
      style={{
        background: 'var(--color-surface-raised)',
        borderRadius: 'var(--radius-md)',
        borderTopLeftRadius: 0,
        borderBottomLeftRadius: 0,
        // The same 4px rule the calendar row carries, so this list reads as what it produces
        // rather than as an abstract set of names.
        borderLeft: '4px solid ' + eventColorVar(type.colorKey),
        padding: 'var(--space-3) var(--space-4)',
        minHeight: 'var(--tap-min)',
        marginInlineStart: isChild ? 'var(--space-5)' : undefined,
        opacity: type.isArchived ? 0.75 : 1,
      }}
    >
      <div className="min-w-0 flex-1">
        {isEditing ? (
          <div className="flex flex-col" style={{ gap: 'var(--space-3)' }}>
            <TextField
              label="Name"
              required
              value={editingName ?? ''}
              error={nameError}
              onChange={(e) => onEditingNameChange?.(e.target.value)}
            />
            <ColorPicker
              value={editingColorKey ?? null}
              onChange={(value) => onEditingColorChange?.(value)}
              inheritsFrom={inheritsFrom ?? null}
              alsoUsedBy={alsoUsedBy ?? new Map()}
              previewName={editingName?.trim() || type.name}
              error={colorError}
            />
            <PriorityPicker
              value={editingPriority ?? null}
              onChange={(value) => onEditingPriorityChange?.(value)}
              inheritsFrom={inheritsFrom ?? null}
              error={priorityError}
            />
            <SuggestionFields
              defaults={editingDefaults ?? NO_DEFAULTS}
              error={defaultsError}
              onChange={(patch) => onEditingDefaultsChange?.(patch)}
            />

            {/* Said before the save, not after. The name and the colour both read through live,
                so a change here reaches every event carrying this type -- which is the intent, and
                a surprise if the count only appears afterwards. The suggestions above are copies
                taken when an event is created and say so themselves. */}
            {type.eventCount > 0 && (
              <p className="t-caption" style={{ color: 'var(--color-text-secondary)', margin: 0 }}>
                The name and colour change what {type.eventCount}{' '}
                {type.eventCount === 1 ? 'event reads' : 'events read'}, past ones included.
              </p>
            )}
            <div className="flex flex-wrap gap-2">
              <Button
                variant="primary"
                onClick={onSaveEdit}
                disabledReason={editingName?.trim() ? undefined : 'Give it a name.'}
              >
                Save
              </Button>
              <Button variant="secondary" onClick={onCancelEdit}>
                Cancel
              </Button>
            </div>
          </div>
        ) : (
          <>
            <span className="t-body-strong" style={{ color: 'var(--color-text-primary)', display: 'block' }}>
              {type.name}
            </span>
            {type.description && (
              <span className="t-caption" style={{ color: 'var(--color-text-secondary)', display: 'block' }}>
                {type.description}
              </span>
            )}
            <span className="t-caption tabular" style={{ color: 'var(--color-text-muted)' }}>
              {type.eventCount === 0
                ? 'Not used yet'
                : `${type.eventCount} ${type.eventCount === 1 ? 'event' : 'events'}`}
            </span>
          </>
        )}
      </div>

      {/* Absent for a member rather than disabled -- there is no permission they are waiting on,
          and the header above already names who acts. */}
      {canManage && !isEditing && (
        <div className="flex shrink-0 items-center gap-1">
          {onMoveUp && <IconAction label={`Move ${type.name} up`} onClick={onMoveUp} icon={ArrowUp} />}
          {onMoveDown && <IconAction label={`Move ${type.name} down`} onClick={onMoveDown} icon={ArrowDown} />}

          {onStartEdit && (
            <button
              type="button"
              className="link-button t-caption"
              aria-label={`Edit ${type.name}`}
              onClick={onStartEdit}
            >
              Edit
            </button>
          )}

          {onArchive && <IconAction label={`Archive ${type.name}`} onClick={onArchive} icon={Archive} />}
          {onRestore && <IconAction label={`Put ${type.name} back`} onClick={onRestore} icon={ArchiveRestore} />}
        </div>
      )}
    </div>
  );
}

/**
 * Which live kinds already read in each colour, so the picker can say "also used by Campout".
 *
 * Keyed on the **resolved** colour rather than the one a row sets itself: a leader picking Pine
 * wants to know what will look like this on the calendar, and a child type that inherits Pine
 * looks exactly like one that chose it. Excludes the type being edited, which is not a duplicate
 * of itself.
 */
function usedBy(live: EventTypeAdmin[], exceptId: string | null): Map<string, string[]> {
  const byColor = new Map<string, string[]>();

  for (const type of live) {
    if (type.id === exceptId) continue;
    byColor.set(type.colorKey, [...(byColor.get(type.colorKey) ?? []), type.name]);
  }

  return byColor;
}

/**
 * The eight swatches, as a 4x2 grid.
 *
 * 56px targets with 12px gaps fit a 360px viewport with no scrolling and no overflow row, and the
 * eight cover a real unit's recurring kinds without forcing reuse. Past eight the hues land under
 * 35 degrees apart and stop being distinguishable in daylight on a phone, which is the actual
 * viewing condition this product is used in.
 *
 * **Selection is never carried by colour alone.** The ring says which one is chosen and a named
 * line underneath says it in words -- "Pine selected" -- because a ring around a swatch is itself
 * a colour-only signal to anyone who cannot separate two of these. The live preview below the grid
 * is the real event card rather than an abstract sample, so a leader judges the result instead of
 * the ingredient.
 *
 * **Duplicates are allowed and warned.** Two kinds may share a colour; the name is what identifies
 * a kind, and refusing would mean a unit with nine kinds could not finish setting them up.
 */
function ColorPicker({
  value,
  onChange,
  inheritsFrom,
  alsoUsedBy,
  previewName,
  error,
}: {
  /** The colour this kind sets itself, or null to take the one it refines. */
  value: EventColorKey | null;
  onChange: (value: EventColorKey | null) => void;
  /** The parent, when there is one. Only then is "the same as that" an available answer. */
  inheritsFrom: EventTypeAdmin | null;
  alsoUsedBy: Map<string, string[]>;
  previewName: string;
  error?: string;
}) {
  const shown = value ?? (inheritsFrom ? inheritsFrom.colorKey : null);
  const sharing = shown ? (alsoUsedBy.get(shown) ?? []) : [];

  return (
    <div className="flex flex-col" style={{ gap: 'var(--space-3)' }}>
      <p className="t-label" style={{ color: 'var(--color-text-muted)', margin: 0 }}>
        Colour on the calendar
      </p>
      <p className="t-caption" style={{ color: 'var(--color-text-secondary)', margin: 0 }}>
        Every event of this kind reads in it, past ones included &mdash; the name always shows
        beside it, so nothing depends on telling two colours apart.
      </p>

      <div
        role="radiogroup"
        aria-label="Colour on the calendar"
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(4, 56px)',
          gap: '12px',
        }}
      >
        {EVENT_COLORS.map((color) => {
          const selected = shown === color && value !== null;

          return (
            <button
              key={color}
              type="button"
              role="radio"
              aria-checked={selected}
              aria-label={EVENT_COLOR_LABELS[color]}
              title={EVENT_COLOR_LABELS[color]}
              onClick={() => onChange(color)}
              style={{
                width: 56,
                height: 56,
                borderRadius: 'var(--radius-md)',
                background: eventColorVar(color),
                border: 'none',
                cursor: 'pointer',
                // 3px ink ring at 3px offset. An outline rather than a border so the swatch keeps
                // its size and the grid does not shift as the selection moves.
                outline: selected ? '3px solid var(--color-brand-ink)' : 'none',
                outlineOffset: '3px',
              }}
            />
          );
        })}
      </div>

      {/* Said in words, under the grid. A ring is a shape, but which swatch it is around is
          exactly the thing somebody with a colour vision deficiency cannot read back. */}
      <p className="t-caption" role="status" style={{ color: 'var(--color-text-secondary)', margin: 0 }}>
        {value
          ? EVENT_COLOR_LABELS[value] + ' selected'
          : inheritsFrom
            ? 'Same as ' + inheritsFrom.name + ' — ' + label(inheritsFrom.colorKey)
            : 'Nothing selected'}
        {inheritsFrom && value && (
          <>
            {' '}
            <button type="button" className="link-button t-caption" onClick={() => onChange(null)}>
              Use {inheritsFrom.name}&rsquo;s instead
            </button>
          </>
        )}
      </p>

      {sharing.length > 0 && (
        // A note, not a refusal. Two kinds sharing a colour is a thing a unit may well want, and
        // the name is what identifies a kind on every surface the colour appears on.
        <p className="t-caption" style={{ color: 'var(--color-text-muted)', margin: 0 }}>
          Also used by {sharing.slice(0, 3).join(', ')}
          {sharing.length > 3 && ` and ${sharing.length - 3} more`}.
        </p>
      )}

      {error && (
        <p className="t-caption" role="alert" style={{ color: 'var(--color-status-blocked)', margin: 0 }}>
          {error}
        </p>
      )}

      <ColorPreview name={previewName} colorKey={shown} />
    </div>
  );
}

/** The palette name for a key, or the neutral's, for a line of prose. */
function label(colorKey: string): string {
  return isEventColor(colorKey) ? EVENT_COLOR_LABELS[colorKey] : EVENT_COLOR_LABELS.graphite;
}

/**
 * The real event card, at the size it renders on the calendar.
 *
 * A swatch on its own tells a leader nothing about the decision they are making: what they are
 * choosing is how a row looks in a list of rows, and that is a 4px rule and a coloured eyebrow
 * against a raised card rather than a 56px block of colour.
 */
function ColorPreview({ name, colorKey }: { name: string; colorKey: string | null }) {
  return (
    <div
      style={{
        background: 'var(--color-surface-raised)',
        borderRadius: 'var(--radius-md)',
        borderTopLeftRadius: 0,
        borderBottomLeftRadius: 0,
        borderLeft: '4px solid ' + eventColorVar(colorKey),
        padding: 'var(--space-3) var(--space-4)',
      }}
    >
      <span className="t-body-strong" style={{ color: 'var(--color-text-primary)', display: 'block' }}>
        Saturday&rsquo;s one
      </span>
      <span className="t-caption tabular" style={{ color: 'var(--color-text-secondary)' }}>
        <span style={{ color: eventColorVar(colorKey), fontWeight: 600 }}>{name}</span> &middot; Sat
        14 Mar &middot; 8:00 AM depart
      </span>
    </div>
  );
}

/**
 * How much this kind matters when the weekly digest has to choose between them — TROOP-200.
 *
 * A `SelectField` beside the colour rather than inside `SuggestionFields`, and the difference is
 * what a type *lends* versus what a type *is*: a suggestion is copied onto an event at creation and
 * belongs to the event from then on, while this reads through live, so changing it changes what
 * every family is mailed about every event of this kind — including the two already on the
 * calendar. Same category as the name and the colour.
 *
 * Three states, so it is a dropdown and not a set of buttons: "leave it to the parent kind" is a
 * real answer meaning this kind says nothing, exactly as an unset colour does.
 *
 * **It exists at all because a flag with no editor is `UnitOwnedToggle`'s failure exactly** — a
 * column, an API route, six read-only badges, and nothing that could ever set it. Nothing would
 * have looked broken.
 */
function PriorityPicker({
  value,
  onChange,
  inheritsFrom,
  error,
}: {
  value: DigestPriority | null;
  onChange: (value: DigestPriority | null) => void;
  /** The type this one refines, so the empty option can say whose answer it is taking. */
  inheritsFrom: EventTypeAdmin | null;
  error?: string;
}) {
  return (
    <SelectField
      label="In the weekly email"
      value={value ?? ''}
      error={error}
      onChange={(e) => onChange(e.target.value === '' ? null : (e.target.value as DigestPriority))}
      hint="The email looks three months ahead and shows the next few of every kind. This decides which kinds come first when there is more than fits."
    >
      <option value="">
        {inheritsFrom ? `Same as ${inheritsFrom.name}` : 'Nothing in particular — ordinary'}
      </option>
      {PRIORITIES.map((p) => (
        <option key={p.key} value={p.key}>
          {p.label}
        </option>
      ))}
    </SelectField>
  );
}

/**
 * Named for what a family would say rather than for the enum. "Major" is a word about the schema;
 * *people plan their month around it* is the question a leader is actually being asked.
 */
const PRIORITIES: { key: DigestPriority; label: string }[] = [
  { key: 'Major', label: 'Families plan around it — show it first' },
  { key: 'Standard', label: 'Ordinary' },
  { key: 'Routine', label: 'Happens all the time — show it last' },
];

/**
 * What a kind suggests for a new event of that kind.
 *
 * These prefill a form and are read back through nothing. Changing them here leaves every event
 * already on the calendar exactly as it is -- which is the whole reason they are copies at the
 * moment a leader asks rather than a live link (§9), and is worth saying on the screen where
 * somebody is about to change one.
 */
function SuggestionFields({
  defaults,
  error,
  onChange,
}: {
  defaults: EventTypeDefaults;
  error?: string;
  onChange: (patch: Partial<EventTypeDefaults>) => void;
}) {
  // Raw, never trimmed: these boxes are controlled, so trimming what a leader is still typing
  // eats the space bar. `EventType.Blank` already trims and nulls a whitespace-only value on the
  // way in, which is the right place for it -- once, at the boundary, rather than per keystroke.
  const text = (value: string) => (value === '' ? null : value);

  return (
    <div className="flex flex-col" style={{ gap: 'var(--space-3)' }}>
      <p className="t-label" style={{ color: 'var(--color-text-muted)', margin: 0 }}>
        What this kind suggests
      </p>
      <p className="t-caption" style={{ color: 'var(--color-text-secondary)', margin: 0 }}>
        Filled into the form when somebody picks this kind, and editable there. Changing these
        leaves every event already on the calendar exactly as it is.
      </p>

      <div className="grid gap-3" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(9rem, 1fr))' }}>
        {/* type="time" produces exactly the reading the API wants -- 19:00, no date and no zone.
            Quarter hours, matching the event form's own step. */}
        <TextField
          label="Usually starts"
          type="time"
          step={15 * 60}
          value={defaults.startTimeOfDay ?? ''}
          onChange={(e) => onChange({ startTimeOfDay: e.target.value || null })}
        />
        <TextField
          label="Usually ends"
          type="time"
          step={15 * 60}
          value={defaults.endTimeOfDay ?? ''}
          onChange={(e) => onChange({ endTimeOfDay: e.target.value || null })}
        />
        <TextField
          label="Or runs for"
          type="number"
          min={1}
          value={defaults.durationMinutes ?? ''}
          error={error}
          onChange={(e) =>
            onChange({ durationMinutes: e.target.value ? Number(e.target.value) : null })
          }
          hint="Minutes. Use this or an end time, not both."
        />
      </div>

      <TextField
        label="Usually at"
        value={defaults.locationName ?? ''}
        onChange={(e) => onChange({ locationName: text(e.target.value) })}
        placeholder="Fellowship Hall"
      />
      <TextField
        label="Address"
        value={defaults.locationAddress ?? ''}
        onChange={(e) => onChange({ locationAddress: text(e.target.value) })}
      />
      <TextAreaField
        label="Write-up to start from"
        rows={3}
        value={defaults.description ?? ''}
        onChange={(e) => onChange({ description: text(e.target.value) })}
        hint="The boilerplate every one of these starts from."
      />

      <p
        className="t-label"
        style={{ color: 'var(--color-text-muted)', margin: 'var(--space-3) 0 0' }}
      >
        What events of this kind need
      </p>
      <p className="t-caption" style={{ color: 'var(--color-text-secondary)', margin: 0 }}>
        Ticked into the form for a new event of this kind, and editable there. This is where saying
        it once is worth it &mdash; a troop meeting never collects a permission slip.
      </p>

      {/* Three states, not two, which is why these are dropdowns and the event form's are
          checkboxes. "Leave it to..." is a real answer: it means this kind says nothing and the
          kind it refines decides, exactly as an unset colour does. A checkbox would have to
          collapse that into a false and start overriding a parent nobody asked it to. */}
      <div className="grid gap-3" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(11rem, 1fr))' }}>
        {TYPE_REQUIREMENTS.map((r) => (
          <SelectField
            key={r.key}
            label={r.label}
            value={defaults[r.key] === null ? '' : defaults[r.key] ? 'yes' : 'no'}
            onChange={(e) =>
              onChange({ [r.key]: e.target.value === '' ? null : e.target.value === 'yes' })
            }
          >
            <option value="">Leave it to the parent kind</option>
            <option value="yes">Yes</option>
            <option value="no">No</option>
          </SelectField>
        ))}
      </div>
    </div>
  );
}

/** The four flags, named as the event form names them so the two screens agree. */
const TYPE_REQUIREMENTS: {
  key:
    | 'requiresRsvp'
    | 'requiresForms'
    | 'requiresPacking'
    | 'requiresTravel'
    | 'requiresProgram';
  label: string;
}[] = [
  { key: 'requiresRsvp', label: "Who's coming" },
  { key: 'requiresForms', label: 'Forms' },
  { key: 'requiresPacking', label: 'Things to bring' },
  { key: 'requiresTravel', label: 'Getting there' },
  { key: 'requiresProgram', label: 'What we are doing' },
];

function IconAction({
  label,
  onClick,
  icon: Icon,
}: {
  label: string;
  onClick: () => void;
  icon: typeof ArrowUp;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      onClick={onClick}
      className="inline-flex items-center justify-center rounded-md"
      style={{
        minHeight: 'var(--tap-min)',
        minWidth: 'var(--tap-min)',
        background: 'none',
        border: 'none',
        color: 'var(--color-text-muted)',
        cursor: 'pointer',
      }}
    >
      <Icon size={16} aria-hidden="true" />
    </button>
  );
}
